// Stimmen-Tabelle für Admin: Voter × Slots in einer Tabelle.
// Zeilen = Voter (mit klickbaren Sternen für Wichtigkeit), Spalten = Slots
// (mit Kronen für Top-3-Tiers, Datum + Uhrzeit zweizeilig). Direkt unter
// dem Header eine Totals-Zeile mit Counts + stacked Score-Bar.
//
// Filter-Bar oben: alle Voter / ab ★★ / nur ★★★.

import type { Appointment } from '../types';
import type { SurveyVote } from '../api';
import { formatDateShort, formatTime } from '../time';
import {
  saveImportance, weightFor, type ImportanceMap,
} from '../voter-importance';
import { el } from './dom';

export type VoteTableOptions = {
  surveyId: string;
  slots: Appointment[];
  votes: SurveyVote[];
  importance: ImportanceMap;
  /** Wird aufgerufen, sobald sich Sterne ändern (Re-Render-Trigger). */
  onImportanceChange: () => void;
  /** Spotlight-Pseudonym (oder null). */
  activePseudonym: string | null;
  setSpotlight: (idx: number | null) => void;
};

type FilterMode = 'all' | 'imp2' | 'imp3';

let filterMode: FilterMode = 'all';

export function renderAdminVoteTable(opts: VoteTableOptions): HTMLElement {
  const root = el('section', { class: 'admin-section admin-vote-table' });
  const head = el('div', { class: 'admin-vote-table-head' });
  head.append(el('h2', { class: 'admin-section-heading' }, 'Stimmen-Tabelle'));

  if (opts.votes.length === 0 || opts.slots.length === 0) {
    root.append(head, el('p', { class: 'admin-empty' },
      'Sobald Antworten eingehen, erscheint hier die Stimmen-Tabelle.'));
    return root;
  }

  // Filter-Bar — Sterne-basiert.
  const filterBar = el('div', { class: 'admin-vote-table-filter' });
  filterBar.append(el('span', { class: 'admin-vote-table-filter-label' }, 'Filter:'));
  const filterDefs: Array<[FilterMode, string]> = [
    ['all',  `Alle (${opts.votes.length})`],
    ['imp2', '★★+'],
    ['imp3', 'nur ★★★'],
  ];
  for (const [val, label] of filterDefs) {
    const btn = el('button', {
      type: 'button',
      class: `admin-vote-table-filter-btn${filterMode === val ? ' is-active' : ''}`,
    }, label) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      if (filterMode === val) return;
      filterMode = val;
      opts.onImportanceChange(); // forciert kompletten Re-Render der Sektion
    });
    filterBar.append(btn);
  }
  head.append(filterBar);
  root.append(head);

  // Voter nach Filter.
  const filteredVotes = opts.votes.filter((v) => {
    const lvl = opts.importance[v.pseudonym] ?? 0;
    if (filterMode === 'imp2') return lvl >= 2;
    if (filterMode === 'imp3') return lvl >= 3;
    return true;
  });

  if (filteredVotes.length === 0) {
    root.append(el('p', { class: 'admin-empty' },
      'Kein Teilnehmer entspricht dem Filter. Vergib oben Sterne, oder wähle „Alle".'));
    return root;
  }

  // Slot-Reihenfolge: nach Datum/Uhrzeit.
  const sortedSlots = [...opts.slots].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return a.startMinutes - b.startMinutes;
  });

  // Pro Slot: Counts + gewichtete Anteile + Tier-Index.
  const stats = computeWeighted(sortedSlots, filteredVotes, opts.importance);
  const tiers = groupByTier(stats);
  const slotIdToTier = new Map<string, number>();
  tiers.forEach((tier, idx) => tier.forEach((s) => slotIdToTier.set(s.slot.id, idx)));

  // Tabelle.
  const scroller = el('div', { class: 'admin-vote-table-scroller' });
  const tbl = el('table', { class: 'vote-table-table' });

  // Header-Zeile mit Kronen.
  const thead = el('thead');
  const headRow = el('tr');
  headRow.append(el('th', { class: 'vote-table-voter-col' }, 'Teilnehmer'));
  for (const slot of sortedSlots) {
    const tierIdx = slotIdToTier.get(slot.id) ?? 99;
    const crowns = Math.max(0, 3 - tierIdx);
    const th = el('th', { class: 'vote-table-slot-col' });
    th.append(
      el('span', { class: 'vote-table-col-crowns' }, crowns > 0 ? renderCrowns(crowns) : ''),
      el('span', { class: 'vote-table-col-day' }, formatDateShort(slot.date)),
      el('span', { class: 'vote-table-col-time' },
        `${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`),
    );
    headRow.append(th);
  }
  thead.append(headRow);
  tbl.append(thead);

  // Totals-Zeile mit Counts + stacked Score-Bar.
  const totRow = el('tr', { class: 'vote-table-totals-row' });
  totRow.append(el('td', { class: 'vote-table-voter-cell' }, 'Stimmen'));
  for (const slot of sortedSlots) {
    const s = stats.find((x) => x.slot.id === slot.id)!;
    const cell = el('td');
    cell.append(
      el('div', { class: 'vote-table-tot-counts' },
        el('span', { class: 'tot-yes' },   `${s.counts.yes}✓`),
        el('span', { class: 'tot-maybe' }, `${s.counts.maybe}?`),
        el('span', { class: 'tot-no' },    `${s.counts.no}✗`),
      ),
      buildScoreBar(s.yesPct, s.maybePct),
    );
    totRow.append(cell);
  }
  tbl.append(totRow);

  // Voter-Zeilen.
  const tbody = el('tbody');
  filteredVotes.forEach((vote) => {
    const lvl = opts.importance[vote.pseudonym] ?? 0;
    const isActive = vote.pseudonym === opts.activePseudonym;
    const row = el('tr', {
      class: `vote-table-voter-row imp-${lvl}${isActive ? ' is-spotlight' : ''}`,
    });
    const nameCell = el('td', { class: 'vote-table-voter-cell' });
    const nameWrap = el('span', { class: 'vote-table-voter-name-wrap' });
    nameWrap.append(
      el('span', { class: 'vote-table-voter-name' }, vote.pseudonym),
      renderStarPicker(vote.pseudonym, lvl, opts),
    );
    // Click auf den Namen → Spotlight (außerhalb der Sterne).
    nameWrap.addEventListener('click', (e) => {
      // Stern-Clicks haben stopPropagation → kommen nicht an.
      e.stopPropagation();
      const realIdx = opts.votes.findIndex((v) => v.pseudonym === vote.pseudonym
        && v.created_at === vote.created_at);
      opts.setSpotlight(isActive ? null : realIdx);
    });
    nameWrap.style.cursor = 'pointer';
    nameCell.append(nameWrap);
    row.append(nameCell);
    for (const slot of sortedSlots) {
      const r = vote.responses[slot.id];
      const cellCls = r === 'yes' ? 'cell-yes' : r === 'maybe' ? 'cell-maybe' : r === 'no' ? 'cell-no' : 'cell-empty';
      const symbol = r === 'yes' ? '✓' : r === 'maybe' ? '?' : r === 'no' ? '✗' : '·';
      row.append(el('td', { class: `vote-table-cell ${cellCls}` }, symbol));
    }
    tbody.append(row);
  });
  tbl.append(tbody);
  scroller.append(tbl);
  root.append(scroller);

  // Hint unter der Tabelle.
  root.append(el('p', { class: 'admin-vote-table-legend' },
    '👑 Top-3-Tiers nach gewichtetem Score (Vielleicht zählt 0,5; ★-Stufen werten Voter höher). ',
    'Klick auf Pseudonym = Spotlight, Klick auf Sterne = Wichtigkeit setzen.',
  ));

  return root;
}

function renderCrowns(count: number): HTMLElement {
  const wrap = el('span', { class: 'crown-row' });
  for (let i = 0; i < count; i++) {
    wrap.append(el('span', { class: `crown-on lvl-${count}` }, '👑'));
  }
  return wrap;
}

function renderStarPicker(
  pseudonym: string,
  current: number,
  opts: VoteTableOptions,
): HTMLElement {
  const wrap = el('span', { class: 'star-row', title: 'Wichtigkeit dieses Teilnehmers (lokal, fließt ins Ranking)' });
  for (let i = 1; i <= 3; i++) {
    const isOn = i <= current;
    const star = el('span', {
      class: `star-cell${isOn ? ` is-on lvl-${current}` : ''}`,
      role: 'button',
      tabindex: '0',
    }, '★');
    const handler = (e: Event) => {
      e.stopPropagation();
      const next = current === i ? 0 : i;
      if (next === 0) delete opts.importance[pseudonym];
      else opts.importance[pseudonym] = next;
      saveImportance(opts.surveyId, opts.importance);
      opts.onImportanceChange();
    };
    star.addEventListener('click', handler);
    star.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(e); }
    });
    wrap.append(star);
  }
  return wrap;
}

function buildScoreBar(yesPct: number, maybePct: number): HTMLElement {
  const bar = el('div', { class: 'score-bar' });
  const yes = el('div', { class: 'score-yes' });
  yes.style.width = `${yesPct * 100}%`;
  const maybe = el('div', { class: 'score-maybe' });
  maybe.style.width = `${maybePct * 100}%`;
  bar.append(yes, maybe);
  return bar;
}

type WeightedStat = {
  slot: Appointment;
  counts: { yes: number; maybe: number; no: number };
  yesPct: number;
  maybePct: number;
  score: number;
};

function computeWeighted(
  slots: Appointment[],
  votes: SurveyVote[],
  importance: ImportanceMap,
): WeightedStat[] {
  const totalW = votes.reduce((sum, v) => sum + weightFor(importance, v.pseudonym), 0);
  return slots.map((slot) => {
    let yes = 0, maybe = 0, no = 0;
    let yesW = 0, maybeW = 0;
    for (const v of votes) {
      const w = weightFor(importance, v.pseudonym);
      const r = v.responses[slot.id];
      if (r === 'yes')   { yes++;   yesW   += w; }
      else if (r === 'maybe') { maybe++; maybeW += w * 0.5; }
      else if (r === 'no')    { no++;   }
    }
    const yesPct = totalW === 0 ? 0 : yesW / totalW;
    const maybePct = totalW === 0 ? 0 : maybeW / totalW;
    return { slot, counts: { yes, maybe, no }, yesPct, maybePct, score: yesPct + maybePct };
  });
}

function groupByTier(stats: WeightedStat[]): WeightedStat[][] {
  // Top-3 distinct scores (Slots ohne Stimmen ausschließen).
  const ranked = [...stats]
    .filter((s) => s.counts.yes + s.counts.maybe + s.counts.no > 0)
    .sort((a, b) => b.score - a.score);
  const tiers: WeightedStat[][] = [];
  let currentKey: number | null = null;
  for (const s of ranked) {
    const key = Math.round(s.score * 1000);
    if (currentKey === null || currentKey !== key) {
      tiers.push([]);
      currentKey = key;
    }
    tiers[tiers.length - 1].push(s);
  }
  return tiers;
}

